import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  from,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { HttpService } from './http.service';
import { TokenRefreshService } from './token-refresh.service';
import { environment } from '@environments/environment';
import { ResetPasswordModel } from '@models/reset-password.model';

import * as Sentry from '@sentry/angular';
import { UserService } from './user.service';
import { CookieWrapper } from './cookie.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { AppRoutes } from '@helpers/app-routes';
import { TranslateService } from '@ngx-translate/core';
import { UserModel } from '@models/user/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authApi = !environment.isFullOnline
    ? `${environment.caisseApiUrl}/auth`
    : `${environment.caisseApiUrl}`;

  private userSubject = new BehaviorSubject<UserModel | null>(null);
  public user$ = this.userSubject.asObservable();

  private isLoggedSubject = new BehaviorSubject<boolean>(false);
  public isLogged$ = this.isLoggedSubject.asObservable();

  private initializationSubject = new BehaviorSubject<boolean>(false);
  public initialized$ = this.initializationSubject.asObservable();

  private readonly httpService = inject(HttpService);
  private readonly cookieService = inject(CookieWrapper);
  private readonly tokenRefreshService = inject(TokenRefreshService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.loadUserFromToken();
  }

  /**
   * Attendre que l'initialisation soit terminée
   */
  async waitForInitialization(): Promise<boolean> {
    return firstValueFrom(
      this.initialized$.pipe(
        filter((initialized) => initialized),
        take(1),
      ),
    );
  }

  /**
   * Vérifier si le service est initialisé
   */
  isInitialized(): boolean {
    return this.initializationSubject.value;
  }

  /**
   * Connexion de l'utilisateur
   * 1. Appelle l'API de login
   * 2. Vérifie que l'utilisateur a les rôles requis
   * 3. Stocke le token dans les cookies
   * 4. Récupère et met en cache les informations utilisateur
   */
  login(email: string, password: string): Observable<UserModel> {
    const REQUIRED_ROLES = ['ROLE_SELF_ADMIN', 'ROLE_SELF_AGENT', 'ROLE_ADMIN'];

    return this.#post(`${this.authApi}/login`, { email, password }).pipe(
      switchMap((res) => {
        // Parser le JWT pour récupérer l'ID utilisateur et les rôles
        const payload = this.parseJwt(res.token);
        if (!payload || !payload.id) {
          return throwError(() => res?.message || 'errors.invalid_credentials');
        }

        // Vérifier que l'utilisateur a au moins un des rôles requis
        const userRoles = payload.roles || [];
        const hasRequiredRole = REQUIRED_ROLES.some((role) => userRoles.includes(role));

        if (!hasRequiredRole) {
          return throwError(() => new Error('errors.unauthorized_access'));
        }

        // Stocker les tokens dans les cookies uniquement si l'utilisateur est autorisé
        this.tokenRefreshService.storeTokens(res.token, res.refresh_token);

        // Un agent qui se (re)connecte met fin à toute session pointeuse en cours
        this.tokenRefreshService.clearCheckInTokens();

        // Récupérer les informations utilisateur
        return this.userService.getUserById(payload.id);
      }),
      tap((user) => {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.username,
        });

        // Mettre en cache l'utilisateur
        this.userSubject.next(user);
        this.isLoggedSubject.next(true);
        this.initializationSubject.next(true);
      }),
      catchError((error) => {
        // En cas d'erreur, nettoyer les cookies et le cache
        this.clearAuthData();
        this.initializationSubject.next(true);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Charger l'utilisateur depuis le token stocké dans les cookies
   * Appelé automatiquement au refresh de l'application
   */
  private loadUserFromToken(): void {
    const REQUIRED_ROLES = ['ROLE_SELF_ADMIN', 'ROLE_SELF_AGENT', 'ROLE_ADMIN'];
    const token = this.tokenRefreshService.getAccessToken();

    if (!token) {
      this.isLoggedSubject.next(false);
      this.initializationSubject.next(true);
      return;
    }

    // Vérifier si le token est valide
    const payload = this.parseJwt(token);
    if (!payload || !payload.id || this.isTokenExpired(payload)) {
      this.clearAuthData();
      this.initializationSubject.next(true);
      return;
    }

    // Vérifier que l'utilisateur a au moins un des rôles requis
    const userRoles = payload.roles || [];
    const hasRequiredRole = REQUIRED_ROLES.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      // L'utilisateur n'a pas les rôles requis, nettoyer les données
      this.clearAuthData();
      this.initializationSubject.next(true);
      return;
    }

    // Récupérer les informations utilisateur avec le token
    this.userService.getUserById(payload.id).subscribe({
      next: (user) => {
        this.userSubject.next(user);
        this.isLoggedSubject.next(true);
        this.initializationSubject.next(true);
        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.username,
        });
      },
      error: (error) => {
        console.error("Erreur lors du chargement de l'utilisateur:", error);
        this.clearAuthData();
        this.initializationSubject.next(true);
      },
    });
  }

  /**
   * Vérifier si l'utilisateur est connecté
   */
  isLogged(): boolean {
    return this.isLoggedSubject.value;
  }

  /**
   * Vérifier de manière asynchrone si l'utilisateur est connecté
   * Utile pour les guards qui doivent attendre l'initialisation
   */
  async isLoggedAsync(): Promise<boolean> {
    await this.waitForInitialization();
    return this.isLogged();
  }

  /**
   * Récupérer l'utilisateur actuel
   */
  getCurrentUser(): UserModel | null {
    return this.userSubject.value;
  }

  /**
   * Récupérer le token actuel
   */
  getToken(): string | null {
    return this.tokenRefreshService.getAccessToken();
  }

  /**
   * Déconnexion
   * Nettoie les cookies et le cache utilisateur
   */
  logout(): void {
    this.clearAuthData();
  }

  /**
   * Mode pointeuse : stocke le jeton réduit et son refresh_token renvoyés
   * par TerminalService.checkInToken. Reste valide même après logout().
   */
  storeCheckInTokens(token: string, refreshToken: string): void {
    this.tokenRefreshService.storeCheckInTokens(token, refreshToken);
  }

  hasCheckInSession(): boolean {
    return !!this.tokenRefreshService.getCheckInToken();
  }

  requestPasswordReset(email: string, demandeAdmin: boolean = false): Observable<any> {
    return this.httpService.post(`${this.authApi}/reset-password/request`, {
      email: email,
      demande_admin: demandeAdmin,
    });
  }

  resetPassword(resetPassword: ResetPasswordModel): Observable<any> {
    return this.httpService.patch(`${this.authApi}/reset-password/reset`, resetPassword);
  }

  getUser(): UserModel | null {
    return this.userSubject.value;
  }

  /**
   * Nettoyer toutes les données d'authentification
   */
  private clearAuthData(): void {
    this.tokenRefreshService.clearTokens();
    this.userSubject.next(null);
    this.isLoggedSubject.next(false);

    Sentry.setUser(null);
  }

  /**
   * Parser un JWT et extraire le payload
   */
  private parseJwt(
    jwt: string | undefined | null,
  ): { exp: number; iat: number; id: number; roles: string[] } | null {
    if (!jwt) return null;
    try {
      const base64Url = jwt.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('JWT invalide:', e);
      return null;
    }
  }

  /**
   * Vérifier si le token est expiré
   */
  private isTokenExpired(payload: { exp: number }): boolean {
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  async #fetchWithRefresh(url: string, options: RequestInit): Promise<Response> {
    const response = await fetch(url, options);
    if (response.status !== 401) {
      return response;
    }

    try {
      const newToken = await firstValueFrom(this.tokenRefreshService.refreshAccessToken());
      const retryHeaders = {
        ...(options.headers as Record<string, string>),
        Authorization: `Bearer ${newToken}`,
      };
      return fetch(url, { ...options, headers: retryHeaders });
    } catch {
      this.handleUnauthorized();
      throw new Error('Session expirée');
    }
  }

  #post(url: string, body: any, requiresAuth: boolean = true): Observable<any> {
    const headers = requiresAuth
      ? this.getAuthHeaders()
      : { 'Content-Type': 'application/json', Accept: 'application/json' };
    const options: RequestInit = {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    };

    const request = requiresAuth ? this.#fetchWithRefresh(url, options) : fetch(url, options);

    return from(
      request.then(async (response) => {
        if (response.status === 401 && requiresAuth) {
          this.handleUnauthorized();
          throw new Error('Session expirée');
        }
        const data = await response.json();
        if (data.code !== undefined && data.message !== undefined) {
          throw new Error(data.message);
        }
        return data;
      }),
    ).pipe(catchError((err) => throwError(() => err.message || err)));
  }

  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Récupération du token Bearer depuis la session
    if (this.cookieService.check('caisse_token')) {
      const token = this.cookieService.get('caisse_token');
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private handleUnauthorized(): void {
    this.tokenRefreshService.clearTokens();
    this.toastr.error(
      this.translate.instant('session.errors.expired_title'),
      this.translate.instant('session.errors.expired_message'),
    );
    void this.router.navigate([AppRoutes.login]);
  }
}
